/* eslint-disable prefer-const */
import { PayloadAction } from "@reduxjs/toolkit";
import { closeAppBackdrop, notify, showAppBackdrop } from "features/app/store/slice";
import { call, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { IApiResponse } from "types/api";
import { IDocPropertiesData, IDocumentExtractData, IDocumentManagementData } from "types/models/document-management";
import { convertObjectToPath, create_UUID, formatPath, getQueryString } from "utils";
import { apiDelete, apiGet, apiPost } from "utils/api";
import DocumentManagementPaths from "../models/paths";
import {
  addNewCrawlingURL,
  addNewCrawlingURLSuccess,
  addNewRegex,
  addNewRegexSuccess,
  cancelRequest,
  createNewFolder,
  deleteFiles,
  deleteFolder,
  downloadFilePDF,
  duplicateFile,
  fetchDocument,
  fetchDocumentFailure,
  fetchDocumentSuccess,
  fetchDocumentTree,
  fetchDocumentTreeSuccess,
  fetchRegexPattern,
  fetchRegexPatternSuccess,
  getChuckOverlap,
  getChuckSize,
  getDataDocProperties,
  getDataDocumentExtract,
  getDataDocuments,
  getFileNameDocumentExtract,
  saveChunk,
  saveChunkFailure,
  saveChunkSuccess,
  saveDocument,
  saveDocumentFailure,
  saveDocumentSuccess,
  submitDataEdit,
  submitDataEditFailure,
  submitDataEditSuccess,
  updateChunk,
  updateChunkFailure,
  updateChunkSuccess,
  uploadFile,
  uploadFileFailure,
  uploadFileSuccess
} from "./slice";

export const apiFetchRegexPattern = async () => {
  return apiGet<any>(formatPath(DocumentManagementPaths.get_regex));
};

function* handleFetchRegexPattern() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiFetchRegexPattern);
    if (response.data) {
      yield put(fetchRegexPatternSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiAddNewRegexPattern = async (new_regex: PayloadAction<any>) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.get_regex), new_regex);
};

function* handleAddNewRegexPattern(action: PayloadAction<any>) {
  try {
    const response: IApiResponse<any> = yield call(apiAddNewRegexPattern, action.payload);
    if (response.data) {
      yield put(addNewRegexSuccess(response.data));
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiAddNewCrawlingURL = async (data: any) => {
  const bodyData = {
    path: convertObjectToPath(data?.path),
    type: data.type,
    files: data?.files
  }
  return apiPost<any>(formatPath(DocumentManagementPaths.add_new_crawling_url), bodyData);
};

function* handleAddNewCrawlingURL(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(apiAddNewCrawlingURL, action.payload);
    if (response.data) {
      yield put(addNewCrawlingURLSuccess(response.data));
      yield put(notify(`Add new crawling url success`, {
        options: { variant: "success" },
      }))
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
    }
    yield put(closeAppBackdrop());
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchDocumentTree = async () => {
  return apiGet<any>(formatPath(DocumentManagementPaths.get_tree_document));
};

function* handleFetchDocumentTree() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiFetchDocumentTree);
    if (response.data) {
      yield put(fetchDocumentTreeSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchDocument = async (query: any) => {
  const queryString = getQueryString({ ...query });
  return apiGet<any>(
    formatPath(DocumentManagementPaths.get_document, queryString)
  );
};

export const apiDownloadFilePDF = async (data: any) => {
  return apiPost<any>(
    formatPath(DocumentManagementPaths.download_file), data
  );
};

function* handleDownloadFilePDF(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiDownloadFilePDF,
      {
        path: convertObjectToPath(action.payload.folderStep),
        filename: action.payload.filename
      }
    );
    if (response.data) {
      const link = document.createElement('a');
      link.href = response.data;
      document.body.appendChild(link);
      link.click();
      link.remove();
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchExtractDocument = async (fileData: any) => {
  const form_data: FormData = new FormData();
  form_data.append("file", fileData.valueName.data);
  form_data.append("filepath", convertObjectToPath(fileData.folderStep));
  form_data.append("replace", fileData.replace);
  form_data.append("chat_page", fileData.chat_page);

  return apiPost<any>(
    formatPath(DocumentManagementPaths.extract_file),
    form_data,
    {
      headers: "multipart/form-data",
    }
  );
};

function* handleFetchExtractFile(action: PayloadAction<any>) {
  try {
    const { response, cancel } = yield race({
      response: call(
        apiFetchExtractDocument,
        action.payload),
      cancel: take(cancelRequest.type),
    });

    if (cancel) {
      yield put(uploadFileFailure());
      return;
    } else if (response.data) {
      if (response.data.duplicate) {
        yield put(duplicateFile(response.data));
      } else {
        yield put(uploadFileSuccess(response.data));
      }
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(uploadFileFailure());
      }
    }
  } catch (e) {
    //
  }
}

export const apiFetchPreviewFile = async (data: any) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.preview_file), data);
};

function* handleFetchPreviewFile(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const documentExtract: IDocumentExtractData = yield select(getDataDocumentExtract);
    const fileNameExtract: IDocumentExtractData = yield select(getFileNameDocumentExtract);
    const response: IApiResponse<any> = yield call(
      apiFetchPreviewFile,
      {
        ...action.payload.data,
        chat_page: action.payload.chat_page,
        original_text: documentExtract.original_text === '' ? documentExtract.document_text : documentExtract.original_text,
        filename: fileNameExtract
      }
    );
    if (response.data) {
      yield put(submitDataEditSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(submitDataEditFailure());
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchSaveDocument = async (data: any) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.save_file), data);
};

function* handleFetchSaveDocument(action: PayloadAction<any>) {
  try {
    const documentExtract: IDocumentExtractData = yield select(getDataDocumentExtract);
    const chunkSizeExtract: IDocumentExtractData = yield select(getChuckSize);
    const chunkOverlapExtract: IDocumentExtractData = yield select(getChuckOverlap);
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiFetchSaveDocument,
      {
        ...action.payload,
        original_text: documentExtract.original_text,
        radio_select: documentExtract.radio_select,
        chunk_size: chunkSizeExtract,
        chunk_overlap: chunkOverlapExtract,
      },
    );
    if (response.data) {
      yield put(saveDocumentSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(saveDocumentFailure());
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchSaveChunk = async (data: any) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.save_chunk), data);
};

export function* handleFetchDocument(action: PayloadAction<any>): Generator<any, void, IApiResponse<any>> {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiFetchDocument,
      action.payload
    );
    if (response.data) {
      if (response.data.type === 'warning') {
        yield put(
          notify(`${response.data.detail}`, {
            options: { variant: response.data.type },
          })
        );
      } else {
        yield put(fetchDocumentSuccess(response.data));
      }
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(fetchDocumentFailure());
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

function* handleSaveChunk(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const documentExtract: IDocumentExtractData = yield select(getDataDocumentExtract);
    
    const { response, cancel } = yield race({
      response: call(apiFetchSaveChunk, {
        data: {
          path: convertObjectToPath(action.payload.value.path),
          files: {
            isDir: false,
            id: create_UUID(),
            name: action.payload.value.file,
            files: []
          },
          save_summary: action.payload.save_summary,
          radio_select: documentExtract.radio_select,
          chat_page: action.payload.chat_page,
        },
        chunk_list: action.payload.chunk_list,
      }),
      cancel: take(cancelRequest.type),
    });

    if (cancel) {
      yield put(saveChunkFailure());
      yield put(closeAppBackdrop());
    } else if (response.data) {
      if (response.data.status_task.status === 'in_progress') {
        yield put(saveChunkSuccess(response.data));
        yield put(closeAppBackdrop());
      } else {
        yield put(closeAppBackdrop());
      }
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(saveChunkFailure());
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiUpdateChunk = async (data: any) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.update_chunk), data);
};

function* handleUpdateChunk(action: PayloadAction<any>) {
  let dataDocProperties: IDocPropertiesData = yield select(getDataDocProperties);

  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiUpdateChunk,
      action.payload
    );
    if (response.data) {
      yield put(notify(`${response.data.detail}`, {
        options: { variant: "success" },
      }))
      yield call(handleFetchDocument, {
        payload: {
          filter_document: dataDocProperties.name,
          chat_page: action.payload.chat_page
        },
        type: fetchDocument.type
      });
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
        yield put(saveDocumentFailure());
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiAddFolder = async (data: any) => {
  return apiPost<any>(formatPath(DocumentManagementPaths.add_document), data);
};

function* handleAddNewFolder(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiAddFolder, {
      path: convertObjectToPath(action.payload.folderStep),
      folder: [{
        ...action.payload.valueName,
        permission: `${action.payload.folderStep.length > 1 ?
          action.payload.folderStep[1].name.toLowerCase() + "-" : ''
          }${action.payload.valueName.name.toLowerCase().replace(" ", "-")}`,
      }],
    });
    if (response.data) {
      yield put(fetchDocumentTreeSuccess(response.data));
      if (action.payload.folderStep.length < 2) {
        yield put(
          notify(`Create Folder ${action.payload.valueName.name} success. Notify to all admin by email`, {
            options: { variant: "success" },
          })
        );
      } else {
        yield put(
          notify(`Create Folder ${action.payload.valueName.name} success`, {
            options: { variant: "success" },
          })
        );
      }
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

function flattenData(data: any) {
  let pdfFileNames: any = [];

  function extractNames(data: any) {
    data.forEach((item: any) => {
      if (item.isDir) {
        extractNames(item.files);
      } else if (item.name.endsWith(".pdf")) {
        pdfFileNames.push(item);
      }
    });
  }
  extractNames(data);
  return pdfFileNames;
}

export const apiDeleteFolder = async (data: any) => {
  return apiDelete<any>(formatPath(DocumentManagementPaths.delete_folder_document), data);
};

function* handleDeleteFolder(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const tree: IDocumentManagementData[] = yield select(getDataDocuments);
    let tempTree;
    tree.map((rData: IDocumentManagementData, idxRoot: number) => {
      if (rData.id === action.payload.folderStep[0].id.toString()) {
        if (
          rData.id ===
          action.payload.folderStep[action.payload.folderStep.length - 1].id
        ) {
          const filterData = rData.files.filter(
            (z) => z.id === action.payload.valueName[0].id
          );
          return (tempTree = filterData);
        } else {
          return {
            ...rData,
            files: rData.files.map((x: IDocumentManagementData) => {
              if (x.id === action.payload.folderStep[1].id.toString()) {
                if (
                  x.id ===
                  action.payload.folderStep[
                    action.payload.folderStep.length - 1
                  ].id
                ) {
                  const filterData = x.files.filter(
                    (z) => z.id === action.payload.valueName[0].id
                  );
                  return (tempTree = filterData);
                } else {
                  return {
                    ...x,
                    files: x.files.map((y: IDocumentManagementData) => {
                      if (y.id === action.payload.folderStep[2].id.toString()) {
                        if (
                          y.id ===
                          action.payload.folderStep[
                            action.payload.folderStep.length - 1
                          ].id
                        ) {
                          const filterData = y.files.filter(
                            (z) => z.id === action.payload.valueName[0].id
                          );
                          return (tempTree = filterData);
                        }
                      }
                      return {
                        ...y,
                      };
                    }),
                  };
                }
              }
              return {
                ...x,
              };
            }),
          };
        }
      }
      return {
        ...rData,
      };
    });
    const response: IApiResponse<any> = yield call(apiDeleteFolder, {
      path: convertObjectToPath(action.payload.folderStep),
      folder: tempTree,
      files: flattenData(tempTree),
    });
    if (response.data) {
      yield put(fetchDocumentTreeSuccess(response.data));
      if (action.payload.folderStep.length < 2) {
        yield put(
          notify(
            `Delete folder ${action.payload.valueName[0].name
            } success. Notify to all admin by email`,
            {
              options: { variant: "success" },
            }
          )
        );
      } else {
        yield put(
          notify(
            `Delete folder ${action.payload.valueName[0].name
            } success`,
            {
              options: { variant: "success" },
            }
          )
        );
      }
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiDeleteFile = async (data: any) => {
  return apiDelete<any>(formatPath(DocumentManagementPaths.delete_file_document), data);
};

function* handleDeleteFiles(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiDeleteFile, {
      path: convertObjectToPath(action.payload.folderStep),
      files: action.payload.valueName
    });
    if (response.data) {
      yield put(fetchDocumentTreeSuccess(response.data));
      yield put(
        notify(
          `Delete ${action.payload.valueName.length} ${action.payload.valueName.length > 1 ? 'files' : 'file'} ${action.payload.valueName.map((x: any) => x.name).join(',')
          } success`,
          {
            options: { variant: "success" },
          }
        )
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export default function* DocumentManagementSaga() {
  yield takeLatest(uploadFile.type, handleFetchExtractFile);
  yield takeLatest(addNewCrawlingURL.type, handleAddNewCrawlingURL);
  yield takeLatest(submitDataEdit.type, handleFetchPreviewFile);
  yield takeLatest(saveDocument.type, handleFetchSaveDocument);
  yield takeLatest(fetchDocumentTree.type, handleFetchDocumentTree);
  yield takeEvery(fetchDocument.type, handleFetchDocument);
  yield takeLatest(createNewFolder.type, handleAddNewFolder);
  yield takeLatest(deleteFolder.type, handleDeleteFolder);
  yield takeLatest(deleteFiles.type, handleDeleteFiles);
  yield takeLatest(saveChunk.type, handleSaveChunk);
  yield takeLatest(downloadFilePDF.type, handleDownloadFilePDF);
  yield takeLatest(fetchRegexPattern.type, handleFetchRegexPattern);
  yield takeLatest(addNewRegex.type, handleAddNewRegexPattern);
  yield takeEvery(updateChunk.type, handleUpdateChunk);
}
